import React from "react";

import * as Styled from "./styles";

import { ReactComponent as ClockIcon } from "shared/assets/svg/clock.svg";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Modal,
  Popover,
  Select,
  SelectChangeEvent,
  Typography,
  ClickAwayListener,
} from "@mui/material";
import { FaMobile, FaMobileScreenButton, FaCircleInfo, FaExclamation } from "react-icons/fa6";
import { IoReceiptOutline } from "react-icons/io5";
import { DEFAULT_AVATAR } from "shared/config/properties";
import { useNavigate } from "react-router-dom";
import { ROUTER } from "shared/constants/router";

import { ReactComponent as MenuIcon } from "shared/assets/svg/menu.svg";
import { Schedule, ScheduleLight, TimeSlot } from "shared/types/Schedule";
import { postDiagnosis } from "requests/Diagnosis";
import { useAuth } from "shared/providers/auth";
//import { Odontograma } from "components/Odontograma";
import { getScheduleDetail, scheduleInit, scheduleRestart } from "requests/Schedule";
import toast from "react-hot-toast";
//import { IOdont } from "shared/types/SendSchedule";
import { Documentation } from "components/Documentation";
import { ClinicalRecordUser } from "components/ClinicalRecord";
import { OdontogramaUser } from "components/OdontogramaUser";
import { getAlerts } from "requests/Alert";
//import { endService } from "requests/RestService";
import { AvalitationUser } from "components/AvaliationGet";
import { DentistHistoryModal } from "components/DentistHistoryModal";
import { ReferralStatusTracker } from "components/Referrals";
import { PatientInfoModal } from "components/PatientInfoModal";

import CalendarImg from "shared/assets/img/calendario_azulclaro.png";

interface IProps {
  data: TimeSlot;
}

// Gera tooltip de informações do app
const getAppTooltip = (item: ScheduleLight): string => {
  if (item.has_app === 1) {
    let tooltip = "✅ App instalado";
    if (item.app_ultimo_acesso) {
      tooltip += `\n📅 Último acesso: ${item.app_ultimo_acesso}`;
    }
    return tooltip;
  }
  return "❌ App Cliente não instalado\n\nOriente o paciente a baixar o app para facilitar a comunicação e engajamento.";
};

// Gera tooltip de condições neurológicas
const getNeuroTooltip = (item: ScheduleLight): string => {
  if (item.has_neuro === 1 && item.neuro_info) {
    let tooltip = "⚠️ Condições Neurológicas e Transtornos\n\n";

    if (item.neuro_info.conditions && item.neuro_info.conditions.length > 0) {
      tooltip += item.neuro_info.conditions.map(c => `• ${c}`).join("\n");
    }

    if (item.neuro_info.other_conditions) {
      tooltip += `\n\n📝 Outras: ${item.neuro_info.other_conditions}`;
    }

    return tooltip;
  }
  return "";
};

// Gera informações do histórico anterior
const getHistoricoInfo = (item: ScheduleLight): { label: string; color: string; tooltip: string } | null => {
  if (item.historico_anterior === 2) {
    return {
      label: "Faltou",
      color: "#d32f2f",
      tooltip: "⚠️ Paciente FALTOU no agendamento anterior"
    };
  }
  if (item.historico_anterior === 1) {
    return {
      label: "Remarcou",
      color: "#ed6c02",
      tooltip: "⚠️ Paciente REMARCOU o agendamento anterior"
    };
  }
  return null;
};

const CardList: React.FC<IProps> = ({ data }) => {
  const navigate = useNavigate();
  const { user, setAlerts, setModalOpen, setOpenCloseAniversario } = useAuth();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [openOdonto, setOpenOdonto] = React.useState<boolean>(false);

  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [typeBraceId, setTypeBraceId] = React.useState<number>(0);
  const [pacientId, setPacientId] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);

  const [loadingInit, setLoadingInit] = React.useState<boolean>(false);

  const [message, setMessage] = React.useState<string>("");
  const [pos, setPos] = React.useState<boolean>(false);

  const [openCloseDocumentation, setOpenCloseDocumentation] =
    React.useState<boolean>(false);

  const [openCloseClinicalRecord, setOpenCloseClinicalRecord] =
    React.useState<boolean>(false);

  const [openCloseAvaliation, setOpenCloseAvaliation] =
    React.useState<boolean>(false);

  const [openDentistHistory, setOpenDentistHistory] =
    React.useState<boolean>(false);
  const [selectedPatientForHistory, setSelectedPatientForHistory] =
    React.useState<{ id: number; name: string } | null>(null);

  const [openReferrals, setOpenReferrals] = React.useState<boolean>(false);
  const [selectedPatientForReferrals, setSelectedPatientForReferrals] =
    React.useState<{ id: number; name: string } | null>(null);

  const [openPatientInfo, setOpenPatientInfo] = React.useState<boolean>(false);
  const [selectedPatientForInfo, setSelectedPatientForInfo] =
    React.useState<ScheduleLight | null>(null);

  const [openRestartConfirm, setOpenRestartConfirm] =
    React.useState<boolean>(false);
  const [selectedScheduleForRestart, setSelectedScheduleForRestart] =
    React.useState<ScheduleLight | null>(null);
  const [loadingRestart, setLoadingRestart] = React.useState<boolean>(false);

  // Estado para controlar qual badge está aberto: { [index]: 'neuro' | 'app' | 'historico' | null }
  const [openBadgePopover, setOpenBadgePopover] = React.useState<{
    [key: number]: 'neuro' | 'app' | 'historico' | null;
  }>({});
  // Armazena o elemento anchor de cada badge
  const [badgeAnchors, setBadgeAnchors] = React.useState<{
    [key: string]: HTMLElement | null;
  }>({});

  const closeModal = () => {
    setOpenModal(false);
    window.location.reload();
    setMessage("");
    setPos(false);
  };

  const handleBadgeClick = (index: number, type: 'neuro' | 'app' | 'historico', event: React.MouseEvent<HTMLElement>) => {
    const key = `${index}-${type}`;
    setBadgeAnchors((prev) => ({
      ...prev,
      [key]: prev[key] ? null : event.currentTarget,
    }));
    setOpenBadgePopover((prev) => ({
      ...prev,
      [index]: prev[index] === type ? null : type,
    }));
  };

  const handleBadgeClose = (index: number) => {
    setOpenBadgePopover((prev) => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
    // Limpa os anchors relacionados a este index
    setBadgeAnchors((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        if (key.startsWith(`${index}-`)) {
          delete newState[key];
        }
      });
      return newState;
    });
  };

  const handleChange = (event: SelectChangeEvent) => {
    setTypeBraceId(Number(event.target.value));
  };

  const init = async (v: ScheduleLight, readInit: string) => {
    setLoadingInit(true);

    localStorage.removeItem("avaliationFormData");
    localStorage.removeItem("clinicalEvaluationFormData");

    try {
      if (readInit !== "C" && readInit !== "T") {
        setLoadingInit(false);
        return false;
      }

      // Busca detalhes completos + inicia atendimento em paralelo
      const [detailRes, initRes] = await Promise.all([
        getScheduleDetail(v.id, user.data.id),
        scheduleInit({ id: user.data.id, idPac: v.pac_id, idSchedule: v.id }),
      ]);

      if (![200, 201].includes(initRes.status)) {
        toast.error(initRes.data.message);
        setLoadingInit(false);
        return false;
      }

      if (![200, 201].includes(detailRes.status)) {
        toast.error("Erro ao carregar detalhes do agendamento.");
        setLoadingInit(false);
        return false;
      }

      const detail = detailRes.data.data;

      // Mescla dados da listagem leve com detalhes completos
      const full: Schedule = {
        ...v,
        ...detail,
        record_id: initRes.data.data.id || detail.record_id,
      };

      // Fallback para schedule_type
      if (full.schedule_type === null || full.schedule_type === undefined) {
        full.schedule_type = full.isOrto === 0 ? 1 : 2;
      }

      console.log("Debug CardList - Verificando redirecionamento:", {
        schedule_type: full.schedule_type,
        is_appraisal: full.is_appraisal,
        budget: full.budget,
        isOrto: full.isOrto,
        classification: full.classification,
        proc_id: full.proc_id,
        proc_name: full.proc_name,
        type: full.type,
      });

      const saveAndNavigate = (route: string) => {
        localStorage.setItem("personAttendance", JSON.stringify(full));
        navigate(route);
      };

      // PRIORIDADE 0: Procedimento 289 = Avaliação Clínica para paciente com orto ativo
      if (full.proc_id === 289) {
        saveAndNavigate(ROUTER.CLINICAL_EVALUATION);
        setLoadingInit(false);
        return false;
      }

      // PRIORIDADE 1: Clínico + avaliação + com orçamento
      if (full.schedule_type === 1 && full.is_appraisal === 1 && full.budget !== null) {
        saveAndNavigate(ROUTER.CLINICAL_EVALUATION);
        setLoadingInit(false);
        return false;
      }

      // PRIORIDADE 2: Clínico + avaliação + sem orçamento
      if (full.schedule_type === 1 && full.is_appraisal === 1 && full.budget === null) {
        saveAndNavigate(ROUTER.AVALIATION);
        setLoadingInit(false);
        return false;
      }

      // PRIORIDADE 3: Clínico + classification 0 → Avaliação inicial
      if (full.schedule_type === 1 && full.classification === 0) {
        saveAndNavigate(ROUTER.AVALIATION);
        setLoadingInit(false);
        return false;
      }

      // PRIORIDADE 4: Clínico genérico
      if (full.schedule_type === 1) {
        saveAndNavigate(ROUTER.CLINICAL);
        setLoadingInit(false);
        return false;
      }

      // PRIORIDADE 5: Ortodontia
      if (full.schedule_type === 2) {
        if (full.isOrto === 1 && (full.type === 0 || full.type === null) && full.classification !== 0 && full.classification !== 1) {
          setPacientId(full.pac_id);
          setOpenModal(true);
          setLoadingInit(false);
          return false;
        }

        if (full.budget != null) {
          const { data: dataAlert, status: statusAlert } = await getAlerts(
            user.data.id,
            full.budget,
            full.pac_id
          );

          if ([200, 201].includes(statusAlert)) {
            const messages: { key: string; message: string }[] = [];

            for (const key in dataAlert.data) {
              if (key === "neurological") continue;
              if (dataAlert?.data[key]?.message) {
                messages.push({ key, message: dataAlert.data[key].message });
              }
            }

            if (messages.length > 0) {
              setAlerts(messages);
              setModalOpen(true);
              setLoadingInit(false);
            }
          }
        }

        if (full.isAniversario === 1) {
          setOpenCloseAniversario(true);
        }

        if (full.classification === 0) {
          saveAndNavigate(ROUTER.AVALIATION);
          setLoadingInit(false);
          return false;
        }

        if (full.classification === 1) {
          saveAndNavigate(ROUTER.FIRST_CONSULT);
          setLoadingInit(false);
          return false;
        }

        if (full.classification === 3) {
          saveAndNavigate(ROUTER.MAINTENANCE);
          setLoadingInit(false);
          return false;
        }
      }

      // Fallback
      console.warn("Schedule sem schedule_type definido, usando lógica fallback");

      if (full.classification === 0) {
        saveAndNavigate(ROUTER.AVALIATION);
        setLoadingInit(false);
        return false;
      }

      if (full.isOrto === 0 && (!full.aparelhos || full.aparelhos.trim() === "")) {
        saveAndNavigate(ROUTER.CLINICAL);
        setLoadingInit(false);
        return false;
      }

      if (full.classification === 1) {
        saveAndNavigate(ROUTER.FIRST_CONSULT);
        setLoadingInit(false);
        return false;
      }

      if (full.classification === 3) {
        saveAndNavigate(ROUTER.MAINTENANCE);
        setLoadingInit(false);
        return false;
      }
    } catch (e) {
      console.error("Erro ao inicializar:", e);
      toast.error("Ocorreu um erro ao inicializar o atendimento.");
    } finally {
      setLoadingInit(false);
    }
  };

  const registerDiagnosis = async () => {
    setLoading(true);

    try {
      const { data: dataDiagnosis, status } = await postDiagnosis({
        id: user.data.id,
        idPac: pacientId,
        type: typeBraceId,
      });

      if ([200, 201].includes(status)) {
        setMessage(dataDiagnosis.message);
        setPos(true);
        setLoading(false);
      } else {
        setMessage(dataDiagnosis.message);
        setPos(true);
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
    }
  };

  const handleRestartClick = (item: ScheduleLight) => {
    setSelectedScheduleForRestart(item);
    setOpenRestartConfirm(true);
    handleClose();
  };

  const handleRestartConfirm = async () => {
    if (!selectedScheduleForRestart) return;

    setLoadingRestart(true);

    try {
      const { data: dataRestart, status } = await scheduleRestart({
        id: user.data.id,
        idSchedule: selectedScheduleForRestart.id,
      });

      if ([200, 201].includes(status)) {
        toast.success(dataRestart.message || "Atendimento reiniciado com sucesso!");
        setOpenRestartConfirm(false);
        setSelectedScheduleForRestart(null);
        window.location.reload();
      } else {
        toast.error(dataRestart.message || "Erro ao reiniciar atendimento");
      }
    } catch (e) {
      console.error("Erro ao reiniciar atendimento:", e);
      toast.error("Ocorreu um erro ao reiniciar o atendimento.");
    } finally {
      setLoadingRestart(false);
    }
  };

  const handleRestartCancel = () => {
    setOpenRestartConfirm(false);
    setSelectedScheduleForRestart(null);
  };

  return (
    <>
      <Styled.Content>
        <Styled.HourBox>
          <Styled.TimeBox>
            <img
              src={CalendarImg}
              width="25px"
              height="25px"
              alt="Calendar Icon"
            />

            <Styled.HourStyle
              ready={data.data.length > 0 && data.data[0].isAtrasado === 0}
            >
              <span>{data.time}</span>

              {data.data.length > 0 && data.data[0].dt_arrival && (
                <span>
                  <ClockIcon width="25px" height="25px" />
                  {data.data[0].dt_arrival.slice(0, 5)}
                </span>
              )}
            </Styled.HourStyle>
          </Styled.TimeBox>
          {data.data.length > 0 ? (
            data.data.map((item, index) => (
              <Styled.BoxSituation situation={item.st_schedule} key={index}>
                <p>
                  {item.inService === 1 && item.classification === 0
                    ? "Em atendimento"
                    : item.st_schedule === "C"
                      ? "Chegou"
                      : item.st_schedule === "T"
                        ? "Chegou atrasado"
                        : item.st_schedule === "A"
                          ? "Agendado"
                          : item.st_schedule === "E"
                            ? "Encaixe"
                            : item.st_schedule === "K"
                              ? "Atendido"
                              : item.st_schedule === "M"
                                ? "Remarcado (Clínica)"
                                : item.st_schedule === "N"
                                  ? "Não Compareceu"
                                  : item.st_schedule === "Y" && "Atendido/Atrasado"}
                </p>
              </Styled.BoxSituation>
            ))
          ) : (
            <Styled.BoxSituation situation={""}>
              <p>Horário Vago</p>
            </Styled.BoxSituation>
          )}
        </Styled.HourBox>

        {data &&
          data.data &&
          data.data.map((item, index) => (
            <React.Fragment key={index}>
              <Styled.BoxInfo>
                {item.pac_name && (
                  <>
                    <Styled.Picture>
                      <Avatar
                        src={
                          item.pac_avatar && item.pac_avatar
                            ? item.pac_avatar
                            : DEFAULT_AVATAR
                        }
                        alt={item.pac_name}
                        sx={{ width: "55px", height: "55px" }}
                      />
                      {item.has_neuro === 1 && (
                        <>
                          <Styled.AccessibilityBadge
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                              e.stopPropagation();
                              handleBadgeClick(index, 'neuro', e);
                            }}
                          >
                            <FaCircleInfo size={12} />
                          </Styled.AccessibilityBadge>
                          <Popover
                            open={openBadgePopover[index] === 'neuro'}
                            anchorEl={badgeAnchors[`${index}-neuro`]}
                            onClose={() => handleBadgeClose(index)}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'center',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'center',
                            }}
                          >
                            <ClickAwayListener onClickAway={() => handleBadgeClose(index)}>
                              <Box sx={{ p: 2, maxWidth: 300 }}>
                                <Typography sx={{ whiteSpace: "pre-line", fontSize: '14px' }}>
                                  {getNeuroTooltip(item)}
                                </Typography>
                              </Box>
                            </ClickAwayListener>
                          </Popover>
                        </>
                      )}
                      <>
                        <Styled.AppBadge
                          $hasApp={item.has_app === 1}
                          onClick={(e: React.MouseEvent<HTMLElement>) => {
                            e.stopPropagation();
                            handleBadgeClick(index, 'app', e);
                          }}
                        >
                          {item.has_app === 1 ? (
                            <FaMobileScreenButton size={12} />
                          ) : (
                            <FaMobile size={12} />
                          )}
                        </Styled.AppBadge>
                        <Popover
                          open={openBadgePopover[index] === 'app'}
                          anchorEl={badgeAnchors[`${index}-app`]}
                          onClose={() => handleBadgeClose(index)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                          }}
                        >
                          <ClickAwayListener onClickAway={() => handleBadgeClose(index)}>
                            <Box sx={{ p: 2, maxWidth: 300 }}>
                              <Typography sx={{ whiteSpace: "pre-line", fontSize: '14px' }}>
                                {getAppTooltip(item)}
                              </Typography>
                            </Box>
                          </ClickAwayListener>
                        </Popover>
                      </>
                      {getHistoricoInfo(item) && (
                        <>
                          <Styled.HistoricoBadge
                            $tipo={item.historico_anterior || 0}
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                              e.stopPropagation();
                              handleBadgeClick(index, 'historico', e);
                            }}
                          >
                            <FaExclamation size={10} />
                          </Styled.HistoricoBadge>
                          <Popover
                            open={openBadgePopover[index] === 'historico'}
                            anchorEl={badgeAnchors[`${index}-historico`]}
                            onClose={() => handleBadgeClose(index)}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'center',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'center',
                            }}
                          >
                            <ClickAwayListener onClickAway={() => handleBadgeClose(index)}>
                              <Box sx={{ p: 2, maxWidth: 300 }}>
                                <Typography sx={{ whiteSpace: "pre-line", fontSize: '14px' }}>
                                  {getHistoricoInfo(item)?.tooltip}
                                </Typography>
                              </Box>
                            </ClickAwayListener>
                          </Popover>
                        </>
                      )}
                    </Styled.Picture>
                    <Styled.Infos>
                      {item.preferencial === 1 && (
                        <Styled.IdPreferencial>
                          Preferencial
                        </Styled.IdPreferencial>
                      )}
                      <Styled.Id>ID {item.pac_id_fant}</Styled.Id>
                      <Styled.Name>{item.pac_name}</Styled.Name>
                      <Styled.TypeConsult>{item.proc_name}</Styled.TypeConsult>
                    </Styled.Infos>

                    {item.pac_name ? (
                      <button
                        onClick={handleClick}
                        style={{
                          backgroundColor: "transparent",
                          border: "none",
                        }}
                      >
                        <MenuIcon />
                      </button>
                    ) : (
                      <div style={{ height: "81px", width: "78.82px" }}></div>
                    )}
                  </>
                )}
              </Styled.BoxInfo>

              {item.st_schedule !== "C" && item.st_schedule !== "T" && (
                <Styled.BoxBtnFake>
                  <Button>INICIAR</Button>
                </Styled.BoxBtnFake>
              )}

              {item.st_schedule === "C" &&
                item.classification === 0 &&
                item.inService !== 0 && (
                  <Styled.BoxBtnFake>
                    <Button>INICIAR</Button>
                  </Styled.BoxBtnFake>
                )}

              {item.st_schedule === "T" &&
                item.classification === 0 &&
                item.inService !== 0 && (
                  <Styled.BoxBtnFake>
                    <Button>INICIAR</Button>
                  </Styled.BoxBtnFake>
                )}

              {item.st_schedule === "C" && item.classification !== 0 && (
                <Styled.BoxBtn>
                  <Button
                    onClick={() => init(item, item.st_schedule)}
                    variant={
                      item.st_schedule === "C" ? "contained" : "outlined"
                    }
                    disabled={loadingInit}
                  >
                    {loadingInit ? <CircularProgress /> : "INICIAR"}
                  </Button>
                </Styled.BoxBtn>
              )}
              {item.st_schedule === "T" && item.classification !== 0 && (
                <Styled.BoxBtn>
                  <Button
                    onClick={() => init(item, item.st_schedule)}
                    variant={
                      item.st_schedule === "T" ? "contained" : "outlined"
                    }
                    disabled={loadingInit}
                  >
                    {loadingInit ? <CircularProgress /> : "INICIAR"}
                  </Button>
                </Styled.BoxBtn>
              )}

              {item.st_schedule === "T" &&
                item.classification === 0 &&
                item.inService !== 1 && (
                  <Styled.BoxBtn>
                    <Button
                      onClick={() => init(item, item.st_schedule)}
                      variant={
                        item.st_schedule === "T" ? "contained" : "outlined"
                      }
                      disabled={loadingInit}
                    >
                      {loadingInit ? <CircularProgress /> : "INICIAR"}
                    </Button>
                  </Styled.BoxBtn>
                )}

              {item.st_schedule === "C" &&
                item.classification === 0 &&
                item.inService !== 1 && (
                  <Styled.BoxBtn>
                    <Button
                      onClick={() => init(item, item.st_schedule)}
                      variant={
                        item.st_schedule === "C" ? "contained" : "outlined"
                      }
                      disabled={loadingInit}
                    >
                      {loadingInit ? <CircularProgress /> : "INICIAR"}
                    </Button>
                  </Styled.BoxBtn>
                )}
            </React.Fragment>
          ))}
      </Styled.Content>
      {data.data
        ?.filter((key) => key.pac_name.length > 0)
        .map((item) => (
          <Menu
            key={item.id}
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem
              onClick={() => {
                setSelectedPatientForInfo(item);
                setOpenPatientInfo(true);
                handleClose();
              }}
              sx={{ fontSize: "17px", padding: "10px", display: "flex", alignItems: "center", gap: "10px" }}
            >
              <FaCircleInfo color="#053779" /> Informações do Paciente
            </MenuItem>

            <MenuItem
              //onClick={() => setOpenCloseDocumentation(true)}
              onClick={() => navigate(`/documentation/${item.pac_id}`)}
              sx={{ fontSize: "17px", padding: "10px" }}
            >
              Documentação
            </MenuItem>

            <MenuItem
              sx={{ fontSize: "17px", padding: "10px" }}
              //onClick={() => setOpenOdonto(true)}
              onClick={() => navigate(`/odontograma-user/${item.pac_id}`)}
            >
              Odontograma
            </MenuItem>
            <MenuItem
              //onClick={() => setOpenCloseClinicalRecord(true)}
              onClick={() => navigate(`/clinical-record-user/${item.pac_id}`)}
              sx={{ fontSize: "17px", padding: "10px" }}
            >
              Ficha Clínica
            </MenuItem>

            <MenuItem
              //onClick={() => setOpenCloseAvaliation(true)}
              onClick={() => navigate(`/avaliation-user/${item.pac_id}`)}
              sx={{ fontSize: "17px", padding: "10px" }}
            >
              Avaliação
            </MenuItem>

            <MenuItem
              onClick={() => navigate(`/clinical-budgets/${item.pac_id}`, { state: { pac_name: item.pac_name, pac_id: item.pac_id } })}
              sx={{ fontSize: "17px", padding: "10px", display: "flex", alignItems: "center", gap: "10px" }}
            >
              <IoReceiptOutline color="#10b981" /> Orçamentos Clínicos
            </MenuItem>

            <MenuItem
              onClick={() => navigate(`/notes/${item.pac_id}`)}
              sx={{ fontSize: "17px", padding: "10px" }}
            >
              Anotações (Conexão)
            </MenuItem>

            <MenuItem
              onClick={() => navigate(`/evolution/${item.pac_id}`)}
              sx={{ fontSize: "17px", padding: "10px" }}
            >
              Evolução
            </MenuItem>

            <MenuItem
              onClick={() => navigate(`/record-physical/${item.pac_id}`)}
              sx={{ fontSize: "17px", padding: "10px" }}
            >
              Ficha Clínica Antiga
            </MenuItem>

            <MenuItem
              onClick={() => {
                setSelectedPatientForHistory({
                  id: item.pac_id,
                  name: item.pac_name,
                });
                setOpenDentistHistory(true);
                handleClose();
              }}
              sx={{ fontSize: "17px", padding: "10px" }}
            >
              Histórico de Dentistas
            </MenuItem>

            <MenuItem
              onClick={() => {
                setSelectedPatientForReferrals({
                  id: item.pac_id,
                  name: item.pac_name,
                });
                setOpenReferrals(true);
                handleClose();
              }}
              sx={{ fontSize: "17px", padding: "10px" }}
            >
              Encaminhamentos
            </MenuItem>

            {(item.st_schedule === "C" || item.st_schedule === "T") &&
              item.inService === 1 && (
                <MenuItem
                  onClick={() => handleRestartClick(item)}
                  sx={{ fontSize: "17px", padding: "10px", color: "#d32f2f" }}
                >
                  Reiniciar Atendimento
                </MenuItem>
              )}

            <Modal
              open={openCloseDocumentation}
              onClose={() => setOpenCloseDocumentation(false)}
            >
              <Box
                sx={{
                  backgroundColor: "white",
                  height: "100%",
                  width: "100%",
                }}
              >
                <Documentation
                  onClick={() => setOpenCloseDocumentation(false)}
                  idPacientParam={item.pac_id}
                />
              </Box>
            </Modal>

            <Modal
              open={openCloseClinicalRecord}
              onClose={() => setOpenCloseClinicalRecord(false)}
            >
              <Box
                sx={{
                  backgroundColor: "white",
                  height: "100%",
                  width: "100%",
                }}
              >
                <ClinicalRecordUser
                  onClick={() => setOpenCloseClinicalRecord(false)}
                  idPacientParam={item.pac_id}
                />
              </Box>
            </Modal>

            <Modal open={openOdonto} onClose={() => setOpenOdonto(false)}>
              <OdontogramaUser
                idPacientParam={item.pac_id}
                onClick={() => setOpenOdonto(false)}
              />
            </Modal>

            <Modal
              open={openCloseAvaliation}
              onClose={() => setOpenCloseAvaliation(false)}
            >
              <AvalitationUser
                idPacientParam={item.pac_id}
                onClick={() => setOpenCloseAvaliation(false)}
              />
            </Modal>
          </Menu>
        ))}

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Styled.BoxModal>
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              {pos ? (
                <>
                  <Typography>{message}</Typography>
                  <Button onClick={closeModal}>Fechar</Button>
                </>
              ) : (
                <>
                  <Typography>
                    Escolha um tipo de aparelho para continuar
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">
                      Tipo de aparelho
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={String(typeBraceId)}
                      label="Tipo de aparelho"
                      onChange={handleChange}
                    >
                      <MenuItem value={1}>Aparelho Fixo</MenuItem>
                      <MenuItem value={2}>Aparelho Móvel</MenuItem>
                      <MenuItem value={3}>Aparelho Invisível</MenuItem>
                    </Select>
                  </FormControl>
                  <Styled.BoxButtons>
                    <Button variant="outlined" onClick={closeModal}>
                      Cancelar
                    </Button>
                    <Button onClick={registerDiagnosis}>Enviar</Button>
                  </Styled.BoxButtons>
                </>
              )}
            </>
          )}
        </Styled.BoxModal>
      </Modal>

      {/* Modal de Histórico de Dentistas */}
      {selectedPatientForHistory && (
        <DentistHistoryModal
          open={openDentistHistory}
          onClose={() => {
            setOpenDentistHistory(false);
            setSelectedPatientForHistory(null);
          }}
          patientId={selectedPatientForHistory.id}
          patientName={selectedPatientForHistory.name}
        />
      )}

      {/* Modal de Encaminhamentos */}
      {selectedPatientForReferrals && (
        <Modal
          open={openReferrals}
          onClose={() => {
            setOpenReferrals(false);
            setSelectedPatientForReferrals(null);
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "95%",
              maxWidth: "900px",
              maxHeight: "90vh",
              bgcolor: "background.paper",
              borderRadius: "12px",
              boxShadow: 24,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ReferralStatusTracker
              pacienteId={selectedPatientForReferrals.id}
              onClose={() => {
                setOpenReferrals(false);
                setSelectedPatientForReferrals(null);
              }}
            />
          </Box>
        </Modal>
      )}

      {/* Modal de Confirmação para Reiniciar Atendimento */}
      <Modal open={openRestartConfirm} onClose={handleRestartCancel}>
        <Styled.BoxModal>
          {loadingRestart ? (
            <CircularProgress />
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                Reiniciar Atendimento
              </Typography>
              <Typography sx={{ mb: 3, textAlign: "center" }}>
                Tem certeza que deseja reiniciar o atendimento
                {selectedScheduleForRestart?.pac_name && (
                  <strong> de {selectedScheduleForRestart.pac_name}</strong>
                )}
                ?
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 3, textAlign: "center", color: "#666" }}
              >
                Esta ação irá resetar o status do atendimento para "Chegou".
              </Typography>
              <Styled.BoxButtons>
                <Button variant="outlined" onClick={handleRestartCancel}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleRestartConfirm}
                >
                  Reiniciar
                </Button>
              </Styled.BoxButtons>
            </>
          )}
        </Styled.BoxModal>
      </Modal>

      {/* Modal de Informações do Paciente */}
      <PatientInfoModal
        open={openPatientInfo}
        onClose={() => {
          setOpenPatientInfo(false);
          setSelectedPatientForInfo(null);
        }}
        patientId={selectedPatientForInfo?.pac_id || 0}
      />
    </>
  );
};

export { CardList };
